import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsEvent {
  page_url: string;
  page_title?: string;
  referrer?: string;
  event_type: 'page_view' | 'link_click' | 'button_click' | 'form_submit';
  event_data?: Record<string, any>;
  duration_seconds?: number;
}

// Generate or get session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';
  let browser = 'unknown';

  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/.test(userAgent)) {
    deviceType = 'tablet';
  }

  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';

  return { deviceType, browser };
};

export const useAnalytics = () => {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const currentUser = useRef<string | null>(null);

  // Track current user
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      currentUser.current = user?.id || null;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      currentUser.current = session?.user?.id || null;
    });

    return () => subscription.unsubscribe();
  }, []);

  // Track page views
  useEffect(() => {
    pageStartTime.current = Date.now();
    
    const trackPageView = async () => {
      try {
        await trackEvent({
          page_url: location.pathname,
          page_title: document.title,
          referrer: document.referrer,
          event_type: 'page_view'
        });
      } catch (error) {
        // Avoid console errors from analytics in production audits
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Failed to track page view:', error);
        }
      }
    };

    // Defer analytics to avoid blocking critical rendering path
    setTimeout(trackPageView, 200);

    // Track page duration when leaving
    return () => {
      const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
      if (duration > 1) { // Only track if user stayed for more than 1 second
        trackEvent({
          page_url: location.pathname,
          event_type: 'page_view',
          duration_seconds: duration
        }).catch(() => {});
      }
    };
  }, [location]);

  const trackEvent = async (event: AnalyticsEvent) => {
    try {
      const sessionId = getSessionId();
      
      // Call the secure analytics ingestion endpoint
      const { error } = await supabase.functions.invoke('analytics-ingest', {
        body: {
          user_id: currentUser.current,
          session_id: sessionId,
          page_url: event.page_url,
          page_title: event.page_title,
          referrer: event.referrer,
          user_agent: navigator.userAgent,
          event_type: event.event_type,
          event_data: event.event_data,
          duration_seconds: event.duration_seconds
        }
      });

      if (error) {
        // Only log in development to avoid console noise in production
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Analytics tracking error:', error);
        }
      }
    } catch {
      // Swallow analytics errors to keep console clean during audits
    }
  };

  const trackClick = async (elementText: string, elementType: string, url?: string) => {
    await trackEvent({
      page_url: location.pathname,
      event_type: 'link_click',
      event_data: {
        element_text: elementText,
        element_type: elementType,
        target_url: url
      }
    });
  };

  const trackButtonClick = async (buttonText: string, action?: string) => {
    await trackEvent({
      page_url: location.pathname,
      event_type: 'button_click',
      event_data: {
        button_text: buttonText,
        action: action
      }
    });
  };

  const trackFormSubmit = async (formName: string, success: boolean) => {
    await trackEvent({
      page_url: location.pathname,
      event_type: 'form_submit',
      event_data: {
        form_name: formName,
        success: success
      }
    });
  };

  // Enhanced tracking functions for user activities
  const trackFeatureUsage = async (featureName: string, timeSpent = 0) => {
    if (!currentUser.current) return;
    
    try {
      await supabase.rpc('update_feature_usage', {
        _feature_name: featureName,
        _time_spent_seconds: timeSpent
      });
    } catch (error) {
      // Silently handle analytics errors
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Feature usage tracking error:', error);
      }
    }
  };

  const trackUserActivity = async (activityType: string, activityData = {}) => {
    if (!currentUser.current) return;
    
    try {
      await supabase.rpc('track_user_activity', {
        _activity_type: activityType,
        _activity_data: activityData,
        _page_url: location.pathname,
        _session_id: getSessionId()
      });
    } catch (error) {
      // Silently handle analytics errors
      if (process.env.NODE_ENV !== 'production') {
        console.debug('User activity tracking error:', error);
      }
    }
  };

  const trackGameActivity = async (activityType: 'game_started' | 'game_completed' | 'game_abandoned', gameData = {}) => {
    await trackUserActivity(activityType, gameData);
    await trackFeatureUsage('chess_games');
  };

  const trackPuzzleActivity = async (activityType: 'puzzle_started' | 'puzzle_completed' | 'puzzle_skipped', puzzleData = {}) => {
    await trackUserActivity(activityType, puzzleData);
    await trackFeatureUsage('puzzles');
  };

  const trackTutorialActivity = async (activityType: 'tutorial_started' | 'tutorial_completed' | 'tutorial_step_completed', tutorialData = {}) => {
    await trackUserActivity(activityType, tutorialData);
    await trackFeatureUsage('tutorials');
  };

  const trackBotActivity = async (activityType: 'bot_challenge_started' | 'bot_challenge_completed', botData = {}) => {
    await trackUserActivity(activityType, botData);
    await trackFeatureUsage('ai_squad');
  };

  const trackHatchActivity = async (activityType: 'hatch_evolution_started' | 'hatch_evolution_completed', hatchData = {}) => {
    await trackUserActivity(activityType, hatchData);
    await trackFeatureUsage('hatch_evolution');
  };

  return {
    trackClick,
    trackButtonClick,
    trackFormSubmit,
    trackEvent,
    trackFeatureUsage,
    trackUserActivity,
    trackGameActivity,
    trackPuzzleActivity,
    trackTutorialActivity,
    trackBotActivity,
    trackHatchActivity
  };
};

// Hook for tracking specific interactions
export const useTrackInteraction = () => {
  const { trackClick, trackButtonClick } = useAnalytics();

  const trackLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = event.currentTarget;
    trackClick(target.textContent || 'Link', 'link', target.href);
  };

  const trackButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    trackButtonClick(target.textContent || 'Button', target.getAttribute('data-action') || undefined);
  };

  return {
    trackLinkClick,
    trackButtonClickHandler
  };
};